import { MoreVerticalOutlineIcon } from '@carboncopyco/icons';
import type { DropdownItemProps } from '@carboncopyco/ui';
import { Dropdown, DropdownItem, TextBase } from '@carboncopyco/ui';
import { getMaxZIndex } from '@carboncopyco/utils';
import classNames from 'classnames';
import { useContext, useState } from 'react';
import { RuleCardContext } from './RuleCardContext';
import useDropdownItemsConfig, {
  type DropdownItemType,
} from './dropdownItemsConfig';

interface DropdownItemConfig {
  type: DropdownItemType;
  label?: DropdownItemProps['label'];
  disabled?: DropdownItemProps['disabled'];
  onClick?: DropdownItemProps['onClick'];
  iconRight?: DropdownItemProps['iconRight'];
  iconLeft?: DropdownItemProps['iconLeft'];
}

export interface RuleCardDropdownProps {
  items: DropdownItemConfig[];
  zIndex?: number;
  triggerElementTestId?: string;
  onDropdownOpen?: (isOpen: boolean) => void;
}

export const RuleCardDropdown = ({
  items,
  zIndex,
  triggerElementTestId,
  onDropdownOpen,
}: RuleCardDropdownProps) => {
  const dropdownItemsConfig = useDropdownItemsConfig();
  const { open: cardOpen } = useContext(RuleCardContext);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>();

  return (
    <Dropdown
      zIndex={zIndex ?? getMaxZIndex() + 1}
      interactive
      align="start"
      open={dropdownOpen}
      onOpenChange={(isOpen) => {
        setDropdownOpen(isOpen);
        onDropdownOpen?.(isOpen);
      }}
      triggerElement={
        <div
          data-testid={triggerElementTestId}
          className={classNames(
            'w-ds-9 flex justify-center text-ds-natural-600',
            dropdownOpen || cardOpen
              ? 'opacity-100'
              : 'group-[.ds-hovered]:opacity-100 group-[.ds-not-hovered]:opacity-0'
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVerticalOutlineIcon size={20} />
        </div>
      }
    >
      {items
        .filter((item) => dropdownItemsConfig[item.type]?.visible ?? true)
        .map(({ type, disabled, onClick, label, iconRight, iconLeft }) => {
          const { icon: Icon, label: defaultLabel } = dropdownItemsConfig[type];
          return (
            <DropdownItem
              key={type}
              label={
                <TextBase
                  label={label ?? defaultLabel}
                  color="text-ds-natural-600"
                />
              }
              iconLeft={
                <div className="text-ds-natural-600">
                  {iconLeft ?? <Icon size={20} />}
                </div>
              }
              onClick={onClick}
              disabled={disabled}
              iconRight={iconRight}
            />
          );
        })}
    </Dropdown>
  );
};
